/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  ActionMenu,
  ActionMenuProps,
  DialogProps,
  ICON_DELETE,
  ICON_FILE_DOWNLOAD,
  ICON_INFO,
  ICON_LOCK,
  ICON_UNARCHIVE,
} from '@squiz/sds';
import clsx from 'clsx';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileStore } from '../../../../../../Services/file-store';
import { isArchive } from '../../../../../../Utilities/isArchive/isArchive';
import { FILE_EXPLORER_STRINGS } from '../../FileExplorer.constants';
import { useFileExplorerStore } from '../../FileExplorer.state';
import { deleteItemsWizardProps } from '../DeleteItemDialogLauncher/DeleteItemDialogLauncher';
import { getExtractDialogProps } from '../ExtractArchiveDialog/utils/getExtractDialogProps';
import { getInformationDialogProps } from '../InformationDialog/utils/getInformationDialogProps';
import { getPermissionsDialogProps } from '../PermissionsDialogLauncher/PermissionsDialogLauncher';
import { FileExplorerItemActionMenuProps } from './FileExplorerItemActionMenu.props';

export const FileExplorerItemActionMenu = ({
  contentType,
  dropdownUp,
  id,
  path,
  type,
  visibility,
  urls,
  virusScanStatus,
}: FileExplorerItemActionMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const { setSelectedRows, setClearSelectedRows } = useFileExplorerStore();
  const { downloadItem } = useFileStore();
  const { updateActiveDialog } = useDialogStore();

  const handleFileRemoval = async (): Promise<void> => {
    // Select the current row so we can use the delete items wizard
    setSelectedRows([
      {
        name: {
          // Only the id and type are actually used in the deletion process
          props: {
            'data-path': path,
            'data-type': type,
            'data-visibility': visibility,
            id,
          },
        },
      },
    ]);
    updateActiveDialog({
      ...deleteItemsWizardProps,
      onClose: () => {
        setClearSelectedRows(true);
      },
    });
  };

  const handleDownload = async (): Promise<void> => {
    await downloadItem({ fileId: id, source: path });
  };

  const handleChangePermission = async (): Promise<void> => {
    const permissionDialogProps: DialogProps = {
      ...getPermissionsDialogProps({ id, type }),
      stateHandler: useDialogStore,
    };
    updateActiveDialog(permissionDialogProps);
  };

  const handleExtract = useCallback(async (): Promise<void> => {
    const extractDialogProps: DialogProps = {
      ...getExtractDialogProps({ fileId: id }),
      stateHandler: useDialogStore,
    };
    updateActiveDialog(extractDialogProps);
  }, [id, updateActiveDialog]);

  const handleInformation = async (): Promise<void> => {
    const informationDialogProps: DialogProps = {
      ...getInformationDialogProps({ id, urls }),
      stateHandler: useDialogStore,
    };
    updateActiveDialog(informationDialogProps);
  };

  const labels = {
    copy: t(FILE_EXPLORER_STRINGS.copy),
    delete: t(FILE_EXPLORER_STRINGS.delete),
    download: t(FILE_EXPLORER_STRINGS.download),
    extract: t(FILE_EXPLORER_STRINGS.extract),
    fileActions: t(FILE_EXPLORER_STRINGS.fileActions),
    information: t(FILE_EXPLORER_STRINGS.information),
    move: t(FILE_EXPLORER_STRINGS.move),
    permissions: t(FILE_EXPLORER_STRINGS.permissions),
    rename: t(FILE_EXPLORER_STRINGS.rename),
  };

  // istanbul ignore next
  const actionMenuProps: ActionMenuProps = {
    buttonLabel: labels.fileActions,
    classes: clsx('file-explorer__item-action-menu', {
      'file-explorer__item-action-menu--up': dropdownUp,
    }),

    itemList: [
      {
        action: async (): Promise<void> => handleDownload(),
        dataID: labels.download,
        icon: ICON_FILE_DOWNLOAD,
        isDisabled: virusScanStatus !== 'scanned',
        key: labels.download,
        label: labels.download,
      },
      // TODO: OPTIMIZE-426
      // {
      //   action: (): void => {},
      //   icon: ICON_EDIT,
      //   key: labels.rename,
      //   label: labels.rename,
      // },
      // TODO: OPTIMIZE-483
      // {
      //   action: (): void => {},
      //   icon: ICON_FILE_COPY,
      //   key: labels.copy,
      //   label: labels.copy,
      // },
      // TODO: OPTIMIZE-415
      // {
      //   action: (): void => {},
      //   icon: ICON_FOLDER,
      //   key: labels.move,
      //   label: labels.move,
      // },
      {
        action: (): Promise<void> => handleChangePermission(),
        dataID: labels.permissions,
        icon: ICON_LOCK,
        key: labels.permissions,
        label: labels.permissions,
      },
      {
        action: async (): Promise<void> => handleExtract(),
        dataID: labels.extract,
        icon: ICON_UNARCHIVE,
        isDisabled: !isArchive(contentType),
        key: labels.extract,
        label: labels.extract,
      },
      {
        action: async (): Promise<void> => handleInformation(),
        dataID: labels.information,
        icon: ICON_INFO,
        key: labels.information,
        label: labels.information,
      },
      {
        action: (): Promise<void> => handleFileRemoval(),
        dataID: labels.delete,
        icon: ICON_DELETE,
        key: labels.delete,
        label: labels.delete,
      },
    ],
    sectionKey: 'file-actions',
  };

  return <ActionMenu {...actionMenuProps} />;
};
